import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import { searchContext } from '../../../../../../context/Context';
import { getSourceList } from '../../../helpers/sourceListHelpers';
import updateParams from '../../../../../../utils/updateParams';
import { SearchContext } from '../../../../../../types';
import SharedDialog from '../../../../../../global/Modal/Modal';

export interface SidebarSourcesProps {
  tab: string;
  source: string;
  sourceWeights: Array<{ name: string; score: number }>;
}

type SourceItem = { name: string; category?: string };

function flattenSourceList(sourceList: Record<string, string[]> | string[]): SourceItem[] {
  if (Array.isArray(sourceList)) {
    return sourceList.map(name => ({ name }));
  }
  const seen = new Set<string>();
  const items: SourceItem[] = [];
  Object.entries(sourceList).forEach(([category, names]) => {
    (names || []).forEach((name: string) => {
      if (!seen.has(name)) {
        seen.add(name);
        items.push({ name, category });
      }
    });
  });
  return items;
}

export function SidebarSources({ tab, source, sourceWeights }: SidebarSourcesProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { restrictSources } = searchContext() as unknown as SearchContext;

  const [search, setSearch] = useState('');
  const [showClearWeightsModal, setShowClearWeightsModal] = useState(false);
  const [pendingSource, setPendingSource] = useState<string | null>(null);

  const sourceList = useMemo(() => {
    const list = getSourceList({ tab, restrictSources: restrictSources ?? undefined });
    return flattenSourceList(list);
  }, [tab, restrictSources]);

  const filteredList = useMemo(() => {
    if (!search.trim()) return sourceList;
    const q = search.trim().toLowerCase();
    return sourceList.filter(item => item.name.toLowerCase().includes(q));
  }, [sourceList, search]);

  const hasCustomWeights = sourceWeights.length > 0;
  const isCustomWeights = sourceWeights.length > 1;

  const isSourceSelected = (sourceName: string): boolean => {
    if (isCustomWeights) {
      return sourceWeights.some(sw => sw.name === sourceName);
    }
    return source === sourceName;
  };

  const proceedWithSourceChange = (sourceName: string) => {
    sessionStorage.removeItem(`sourceWeights_${tab}`);
    const queryString = updateParams({ source: sourceName });
    const newUrl = `${location.pathname}?${queryString}`;
    if (hasCustomWeights) {
      window.location.href = newUrl;
    } else {
      navigate(newUrl, { replace: true });
    }
  };

  const handleSelectSource = (sourceName: string) => {
    const isPartOfWeights = sourceWeights.some(sw => sw.name === sourceName);
    if (hasCustomWeights && !isPartOfWeights) {
      setPendingSource(sourceName);
      setShowClearWeightsModal(true);
      return;
    }
    proceedWithSourceChange(sourceName);
  };

  const handleSelectAll = () => {
    if (hasCustomWeights) {
      setPendingSource('ALL');
      setShowClearWeightsModal(true);
      return;
    }
    proceedWithSourceChange('ALL');
  };

  const handleClear = () => {
    if (hasCustomWeights) {
      setPendingSource('ALL');
      setShowClearWeightsModal(true);
      return;
    }
    proceedWithSourceChange('ALL');
  };

  const handleConfirmClearWeights = () => {
    if (pendingSource) {
      proceedWithSourceChange(pendingSource);
    }
    setShowClearWeightsModal(false);
    setPendingSource(null);
  };

  const handleCancelClearWeights = () => {
    setShowClearWeightsModal(false);
    setPendingSource(null);
  };

  const selectedCount = isCustomWeights ? sourceWeights.length : 1;

  return (
    <>
      <SharedDialog
        isOpen={showClearWeightsModal}
        onClose={handleCancelClearWeights}
        title="Clear My Score?"
        description="You have a My Score configuration applied. Selecting a different source will clear it."
        confirmText="Yes, Clear My Score"
        cancelText="Cancel"
        hasConfirmBtn
        onConfirm={handleConfirmClearWeights}
      >
        <div className="mt-3">
          <p className="text-sm text-gray-600">
            The current My Score configuration will be removed and the source will be changed to{' '}
            <strong>{pendingSource === 'ALL' ? 'All' : pendingSource?.replace(/_/g, ' ')}</strong>.
          </p>
        </div>
      </SharedDialog>

      <div className="flex flex-col h-full min-h-0 min-w-0 overflow-hidden">
        <div className="flex-shrink-0 px-3 py-2">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/4 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter sources..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center justify-between px-3 pb-2">
          <span className="text-xs text-gray-500">{selectedCount} selected</span>
          {hasCustomWeights && (
            <div className="flex gap-1">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                All
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={handleClear}
                className="text-xs font-medium text-gray-600 hover:text-gray-800"
              >
                Clear
              </button>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto min-h-0 space-y-0.5">
          {filteredList.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">No sources match.</p>
          ) : (
            filteredList.map(item => {
              const isSelected = isSourceSelected(item.name);
              return (
                <label
                  key={item.name}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer group transition-colors hover:bg-gray-100"
                  style={isSelected ? { backgroundColor: 'rgba(236, 42, 153, 0.08)' } : undefined}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleSelectSource(item.name)}
                    className="h-4 w-4 appearance-none rounded-full cursor-pointer transition-colors focus:outline-none focus:ring-0"
                    style={{
                      border: `1px solid ${isSelected ? '#EC2A99' : '#CFCFE4'}`,
                      backgroundColor: isSelected ? '#EC2A99' : '#FFFFFF',
                      boxShadow: isSelected ? 'inset 0 0 0 3px #FFFFFF' : 'none',
                    }}
                  />
                  <span className="text-sm text-gray-800 truncate flex-1">
                    {item.name.replace(/_/g, ' ')}
                  </span>
                  {item.category && (
                    <span className="text-xs text-gray-400 flex-shrink-0">{item.category}</span>
                  )}
                </label>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
